import { controlIcons } from '../../../constants/assets'

import { FilterField } from '../../../components/ui/FilterField'
import { SearchInput } from '../../../components/ui/SearchInput'
import { Select } from '../../../components/ui/Select'
import { DateRangePicker } from '../../../components/ui/DateRangePicker'
import { AdvancedFilterDropdown } from './AdvancedFilterDropdown'
import {
  serviceOptions,
  type BookingsFilters,
} from '../../../data/bookingFilters'

type BookingsFilterBarProps = {
  filters: BookingsFilters
  onChange: (filters: BookingsFilters) => void

  // Applies Search Booking ID, Service Type,
  // Source Name and Date preset filters.
  onApply: () => void

  // Used by filters that already have their own Apply button:
  // Advanced Filter and Custom Date Range.
  onImmediateChange: (
    changes: Partial<BookingsFilters>
  ) => void

  onNewBooking: () => void
}

export const BookingsFilterBar = ({
  filters,
  onChange,
  onApply,
  onImmediateChange,
  onNewBooking,
}: BookingsFilterBarProps) => (
  <div
    className="
      grid grid-cols-1 gap-x-[14px] gap-y-[14px]
      md:grid-cols-2
      xl:grid-cols-[minmax(240px,1.2fr)_minmax(220px,1fr)_minmax(220px,1fr)_206px]
    "
  >
    {/* ROW 1 - COLUMN 1 */}
    <FilterField label="Search Booking ID">
      <SearchInput
        label="Search Booking ID"
        onChange={(query) =>
          onChange({
            ...filters,
            query,
          })
        }
        placeholder="Search here...."
        value={filters.query}
      />
    </FilterField>

    {/* ROW 1 - COLUMN 2 */}
    <FilterField label="Service Type">
      <Select
        label="Service Type"
        onChange={(service) =>
          onChange({
            ...filters,
            service,
          })
        }
        options={serviceOptions}
        value={filters.service}
      />
    </FilterField>

    {/* ROW 1 - COLUMN 3 */}
   <FilterField label="Filter">
      <AdvancedFilterDropdown
        value={filters.quickFilter}
        onChange={(quickFilter) => {
          // Keep UI/draft value updated
          onChange({
            ...filters,
            quickFilter,
          })

          // This filter already has its own
          // "Apply Filters" button.
          onImmediateChange({
            quickFilter,
          })
        }}
      />
    </FilterField>

    {/* ROW 1 - COLUMN 4 */}
    <button
      type="button"
      onClick={onNewBooking}
      className="
        flex h-[65px] w-full items-center justify-center
        gap-[4px] self-end rounded-[12px]
        bg-dash-primary
      "
    >
      <img
        alt=""
        aria-hidden
        className="block size-[24px] max-w-none"
        src={controlIcons.add}
      />

      <span className="whitespace-nowrap font-host text-[16px] font-medium text-white">
        New Booking
      </span>
    </button>

    {/* ROW 2 - COLUMN 1 */}
{/* <FilterField label="Source No.">
  <SearchInput
    label="Source No."
    onChange={(sourceNumber) =>
      onChange({
        ...filters,
        sourceNumber,
      })
    }
    placeholder="Search source no...."
    value={filters.sourceNumber}
  />
</FilterField> */}

    {/* ROW 2 - COLUMN 2 */}
    <FilterField label="Source Name">
      <SearchInput
        label="Source Name"
        onChange={(sourceName) =>
          onChange({
            ...filters,
            sourceName,
          })
        }
        placeholder="Search source name...."
        value={filters.sourceName}
      />
    </FilterField>

    {/* ROW 2 - COLUMN 3 */}
    <FilterField label="Date Range">
      <DateRangePicker
        label="Date Range"
        onChange={(rangeId) => {
          // Always update the selected value shown in the field
          onChange({
            ...filters,
            rangeId,
          })

          // Custom date already has its own Apply button,
          // so apply it immediately after that button is clicked.
          if (rangeId.startsWith('custom:')) {
            onImmediateChange({
              rangeId,
            })
          }

          // Quick presets such as:
          // Today / This Week / This Month / Last 30 Days
          // are NOT applied here.
          // They wait for the main Apply button.
        }}
        value={filters.rangeId}
        variant="field"
      />
    </FilterField>

    {/* ROW 2 - COLUMN 4 - Apply selected filters */}
      <button
        type="button"
        onClick={onApply}
        className="
          flex h-[65px] w-full items-center justify-center
          self-end rounded-[12px]
          bg-[#bad67e]
          font-host text-[16px] font-semibold text-black
          transition-all duration-200
          hover:bg-[#aecb70]
          active:scale-[0.98]
        "
      >
        Apply
      </button>
  </div>
)